import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, LockKeyhole, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function AuthModal({ open, onClose }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        setSent(true);
        return;
      }
      if (mode === "login") await login(username, password);
      else await register(username, password, email);
      toast.success(mode === "login" ? "Welcome back — your organizer is loaded." : "Account created — your organizer now saves automatically.");
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="auth-modal">
          <div className="absolute inset-0 bg-[#FAF6EF]/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="relative glass rounded-2xl w-full max-w-md p-7 sm:p-8 shadow-[0_8px_32px_rgba(58,46,37,0.15)] max-h-[92vh] overflow-y-auto">
            <button onClick={onClose} data-testid="auth-close-btn"
              className="absolute top-4 right-4 text-[#8C7562] hover:text-[#3A2E25] transition-colors">
              <X size={18} />
            </button>
            <div className="w-11 h-11 rounded-xl bg-[#D4A0B4]/15 border border-[#D4A0B4]/40 flex items-center justify-center mb-5">
              <LockKeyhole size={18} className="text-[#D4A0B4]" />
            </div>

            {mode === "forgot" ? (
              <>
                <h3 className="f-display text-2xl font-medium tracking-tight mb-1">Reset your password</h3>
                <p className="text-sm text-[#8C7562] mb-6 leading-relaxed">
                  Enter the email on your account and we'll send a one-hour link to choose a new password.
                </p>
                {sent ? (
                  <div className="rounded-xl border border-[#E8DCCB] bg-[#FFF3F8] p-4 text-sm text-[#3A2E25] leading-relaxed" data-testid="forgot-sent">
                    If an account exists for <strong>{email}</strong>, a reset link is on its way. Check your inbox (and spam folder).
                  </div>
                ) : (
                  <form onSubmit={submit} className="flex flex-col gap-4">
                    <input className="field" type="email" placeholder="Email address" value={email} autoComplete="email" required
                      onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
                    {error && <p className="text-xs text-[#A8785A] font-medium" data-testid="auth-error">{error}</p>}
                    <button type="submit" disabled={busy} className="btn-primary justify-center disabled:opacity-60" data-testid="forgot-submit-btn">
                      {busy ? "Sending…" : "Email me a reset link"}
                    </button>
                  </form>
                )}
                <button type="button" onClick={() => switchMode("login")} data-testid="forgot-back-btn"
                  className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#8C7562] hover:text-[#3A2E25] transition-colors">
                  <ArrowLeft size={13} /> Back to log in
                </button>
              </>
            ) : (
              <>
                <h3 className="f-display text-2xl font-medium tracking-tight mb-1">Secure Cloud Sync</h3>
                <p className="text-sm text-[#8C7562] mb-6 leading-relaxed">
                  Pick a username and password. Your entire organizer autosaves and follows you to any device.
                </p>
                <div className="flex gap-2 mb-6 p-1 rounded-full bg-[#FAF6EF] border border-[#E8DCCB]">
                  {["login", "register"].map((m) => (
                    <button key={m} type="button" onClick={() => switchMode(m)}
                      data-testid={`auth-tab-${m}`}
                      className={`flex-1 rounded-full py-2 text-xs font-bold uppercase tracking-wider transition-colors ${mode === m ? "bg-[#A8785A] text-[#FAF6EF]" : "text-[#8C7562] hover:text-[#3A2E25]"}`}>
                      {m === "login" ? "Log In" : "Create Account"}
                    </button>
                  ))}
                </div>
                <form onSubmit={submit} className="flex flex-col gap-4">
                  <input className="field" placeholder="Username" value={username} autoComplete="username"
                    onChange={(e) => setUsername(e.target.value)} data-testid="auth-username-input" />
                  {mode === "register" && (
                    <input className="field" type="email" placeholder="Email (for receipts & password resets)" value={email} autoComplete="email" required
                      onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input" />
                  )}
                  <input className="field" type="password" placeholder="Password (6+ characters)" value={password}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    onChange={(e) => setPassword(e.target.value)} data-testid="auth-password-input" />
                  {error && <p className="text-xs text-[#A8785A] font-medium" data-testid="auth-error">{error}</p>}
                  <button type="submit" disabled={busy} className="btn-primary justify-center disabled:opacity-60"
                    data-testid="auth-submit-btn">
                    {busy ? "One moment…" : mode === "login" ? "Log In & Resume" : "Create My Organizer"}
                  </button>
                </form>
                {mode === "login" && (
                  <button type="button" onClick={() => switchMode("forgot")} data-testid="auth-forgot-btn"
                    className="mt-4 w-full text-center text-xs font-semibold text-[#8C7562] hover:text-[#A8785A] transition-colors">
                    Forgot your password?
                  </button>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
